
import $ from 'jquery';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import VueSimpleContextMenu from 'vue-simple-context-menu';
import 'vue-simple-context-menu/dist/vue-simple-context-menu.css';
import Toasted from 'vue-toasted';
import { handleError } from '../../../api';
import { blobDownload, formatDate, formatDateTime, formatDateTimeSeconds, WindowType } from '../../../util';
import {
  Account,
  Attachment,
  AttributesRequest,
  Board,
  Card,
  CardAttribute,
  CardDuration,
  CardLabel,
  Label,
  LabelRequest,
  ListItem, Media,
  Media2,
  PublicRequest,
  Responsible,
  ResponsibleRequest,
  SystemAttributesRequest,
  Ticket,
  AskModal,
} from '../../backend/backendapi';
import { KanbanState } from '../kanban.model';
import { getSharedInfoCard, isOwn, isPrivate } from '../kanban.util';
import AttributesModal from './attributes/modal/attributes.controller';
import { SystemAttributes } from './attributes/modal/attributes.model';
import DetailsContent from './detailscontent/DetailsContent';
import AttributesEditModal from './attributes/modal/AttributesEditModal';
import AskModal from '../../AiAsk/AskModal';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  name: 'DetailsModal',
  components: {
    AttributesEditModal,
    DatetimeModal: require('./datetime/datetime.vue.html').default,
    DetailsContent,
    Dropzone: require('../dropzone/dropzone.vue.html').default,
    DurationModal: require('./duration/duration.vue.html').default,
    LabelModal: require('./label/labelmodal.vue.html').default,
    MoveModal: require('./move/move.vue.html').default,
    ResponsibleModal: require('./responsible/responsiblemodal.vue.html').default,
    ShareCardModal: require('./sharecard/sharecard.vue.html').default,
    AskModal: require('../../AiAsk/AskModal.vue').default,
  },
  directives: {},
})
export default class DetailsModal extends Vue {
  @Prop({ required: true })
  modalId!: string;
  @Prop({ required: true })
  state!: KanbanState;
  @Prop({ required: true })
  board!: Board;
  @Prop({ required: true })
  flow!: Ticket;
  @Prop({ required: true })
  card!: Card;
  @Prop({ default: false })
  readonly!: boolean;

  private editName: string | null = null;
  
  @Watch('card', { deep: true }) private cardNameListener(newCard: Card, oldCard: Card) {
    if (oldCard.id !== newCard.id) {
      this.editName = null;
    }
  }

  private newComment: string = '';
  private newTaskItem: string | null = null;
  private attachmentContextMenuOptions = [{ id: 'delete', name: 'Delete' }];
  private attachmentContextMenu!: VueSimpleContextMenu;
  private fileUploadField!: HTMLInputElement;
  private media: Media[] | null = null;
  private dragging: number = 0; //needs to be a counter, as the @dragenter and @dragleave events are also fired for every child element, 0 means not dragged over, >0 means dragged over
  savingAttributes = false;

  getSharedInfoCard = getSharedInfoCard;

  mounted(): void {
    this.attachmentContextMenu = this.$refs.attachmentContextMenu;
    this.fileUploadField = this.$refs.fileUploadField as HTMLInputElement;
  }

  openModal(): void {
    $('#' + this.modalId).modal('show');

    //log card read
    this.logReadConfirmation();
  }

  closeModal(): void {
    $('#' + this.modalId).modal('hide');

    //log card read, the card may have been changed (e.g. task item change, etc.) so we update the read confirmation here as well
    this.logReadConfirmation();
  }

  private logReadConfirmation(): void {
    //log card read
    if (this.board.enableReadConfirmations) {
      const card = this.card;
      this.state.cardsAPI.updateReadConfirmation(card.id)
          .then(confirmation => {
            const index = card.readConfirmations!.findIndex(e => e.userId === confirmation.userId);
            if (index > -1) {
              card.readConfirmations!.splice(index, 1, confirmation);
            } else {
              card.readConfirmations!.push(confirmation);
            }
          });
    }
  }

  get isAiEnabled(): boolean {
    return window.application.check2.isAi;
  }

  private get taskProgress(): number {
    if (!this.card.taskItems)
      return 0;
    return this.card.taskItems.filter(e => e.status === 'DONE').length / this.card.taskItems.length * 100;
  }

  private get responsible(): string {
    if (!this.card.responsibles?.length)
      return '';
    return this.card.responsibles.map(e => e.user?.firstName + ' ' + e.user?.lastName).reduce((l, r) => l + ', ' + r);
  }

  private get labelModalId(): string {
    return 'labelmodal' + this.card.id;
  }

  private get duedateModalId(): string {
    return 'duedatemodal' + this.card.id;
  }

  private get moveModalId(): string {
    return 'movemodal' + this.card.id;
  }

  private get shareModalId(): string {
    return 'sharecardmodal' + this.card.id;
  }

  private get durationsModalId(): string {
    return 'durationmodal' + this.card.id;
  }

  private get canDurations(): boolean {
    return window.application.permissions.kanban.allowTimeRecording;
  }

  private get canDelete(): boolean {
    let b = this.state.canDeleteCards;
    b ||= isOwn(this.board) && isPrivate(this.state, this.board);
    return b;
  }

  private get lockeddown(): boolean {
    return this.board.lockdownDelay! >= 0 && this.card.createdOn!.getTime() + this.board.lockdownDelay! * 1000 < new Date().getTime();
  }

  private close(): void {
  }

  private renameCard(): void {
    if (!this.editName) {
      return;
    }

    let card = this.card.clone();
    card.name = this.editName;

    //save to backend
    this.state.cardsAPI.updateCardName(card)
        .then(res => {
          this.$toasted.info(this.$t('txtSaved').toString());
          this.card.name = card.name;
          this.card.updatedOnForReadConfirmation = res.result!.updatedOnForReadConfirmation;
          this.editName = null;
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private addLabel(label: Label): void {
    let l = new CardLabel();
    l.label = label;
    l.labelId = label.id;
    l.cardId = this.card.id;

    //save to backend
    let req = new LabelRequest();
    req.cardId = this.card.id!;
    req.labelId = label.id!;
    req.add = true;
    this.state.cardsAPI.updateLabel(req)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          if (!this.card.cardLabels)
            this.card.cardLabels = [];
          this.card.cardLabels.push(l);
          this.card.updatedOnForReadConfirmation = new Date();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private removeLabel(label: Label): void {
    const card = this.card;
    let index = (card.cardLabels ?? []).findIndex(e => e.labelId === label.id);
    if (index > -1) {
      //save to backend
      let req = new LabelRequest();
      req.cardId = this.card.id!;
      req.labelId = label.id!;
      req.add = false;
      this.state.cardsAPI.updateLabel(req)
          .then(_ => {
            this.$toasted.info(this.$t('txtSaved').toString());
            card.cardLabels?.splice(index!, 1);
            card.updatedOnForReadConfirmation = new Date();
          })
          .catch(e => handleError(this.$toasted, this.$i18n, e));
    }
  }

  private createAttachment(): Promise<void> {
    const file = this.fileUploadField.files && this.fileUploadField.files[0];
    if (!file)
      return Promise.resolve();
    return this.createAttachmentFromFile(file);
  }

  private createAttachmentFromFile(file: File): Promise<void> {
    const card = this.card;

    //save to backend
    return this.state.attachmentAPI.create(this.card.id, { data: file, fileName: file.name })
        .then(attachment => {
          this.$toasted.info(this.$t('txtSaved').toString());
          if (!card.attachments)
            card.attachments = [];
          card.attachments.push(attachment.result!);
          card.updatedOnForReadConfirmation = new Date();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private createAttachmentDropzoneFn(file: File): boolean {
    this.createAttachmentFromFile(file);
    return true;
  }

  private deleteAttachment(attachment: Attachment): void {
    const card = this.card;
    const index = (card.attachments ?? []).findIndex(e => e.id === attachment.id);
    if (index > -1) {
      const attach = card.attachments![index];

      //save to backend
      this.state.attachmentAPI.delete(attach.id, card.id)
          .then(_ => {
            this.$toasted.info(this.$t('txtDeleted').toString());
            card.attachments?.splice(index, 1);
            card.updatedOnForReadConfirmation = new Date();
          })
          .catch(e => handleError(this.$toasted, this.$i18n, e));
    }
  }

  private duedateChanged(date: Date): void {
    const card = this.card;
    let apicard = card.clone();
    apicard.dueDate = date;

    //save to backend
    this.state.cardsAPI.updateDueDate(apicard)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          card.dueDate = date;
          card.updatedOnForReadConfirmation = new Date();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private responsibleAdd(user: ListItem): void {
    let r = new Responsible();
    r.user = user;
    r.userId = user.id;
    r.cardId = this.card.id;
    r.assignedOn = new Date();

    //save to backend
    let req = new ResponsibleRequest();
    req.cardId = this.card.id!;
    req.userId = user.id!;
    req.add = true;
    this.state.cardsAPI.updateResponsible(req)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          if (!this.card.responsibles)
            this.card.responsibles = [];
          this.card.responsibles.push(r);
          this.card.updatedOnForReadConfirmation = new Date();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private responsibleRemove(responsible: Responsible): void {
    const card = this.card;
    let index = (card.responsibles ?? []).findIndex(e => e.userId === responsible.userId);
    if (index > -1) {
      //save to backend
      let req = new ResponsibleRequest();
      req.cardId = this.card.id!;
      req.userId = responsible.user!.id!;
      req.add = false;
      this.state.cardsAPI.updateResponsible(req)
          .then(_ => {
            this.$toasted.info(this.$t('txtSaved').toString());
            card.responsibles?.splice(index!, 1);
            card.updatedOnForReadConfirmation = new Date();
          })
          .catch(e => handleError(this.$toasted, this.$i18n, e));
    }
  }

  private durationAdd(dur: CardDuration): void {
    if (!dur.userId) {
      return;
    }

    dur.cardId = this.card.id!;

    //save to backend
    this.state.cardsAPI.addDuration(dur)
        .then(res => {
          this.$toasted.info(this.$t('txtSaved').toString());
          if (!this.card.durations)
            this.card.durations = [];
          this.card.durations.push(res.result!);
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private async attributesSave(attrs: CardAttribute[], sysAttrs: SystemAttributes): Promise<void> {
    this.savingAttributes = true;
    try {
      //attrs
      let req = new AttributesRequest({ cardId: this.card.id!, attributes: attrs });
      await this.state.cardsAPI.updateAttributes(req);
      if (!this.card.attributes) {
        this.card.attributes = [];
      }
      this.card.attributes.splice(0);
      this.card.attributes.push(...attrs);

      //sysattrs
      let req2 = new SystemAttributesRequest({
        cardId: this.card.id!,
        cellIdSet: true, cellId: sysAttrs.cellId,
        activityIdSet: true, activityId: sysAttrs.activityId,
      });
      await this.state.cardsAPI.updateSystemAttributes(req2);
      this.card.cellId = sysAttrs.cellId;
      this.card.activityId = sysAttrs.activityId ?? 0; //card.activityId is nonnull

      this.card.updatedOnForReadConfirmation = new Date();

      this.$toasted.info(this.$t('txtSaved').toString());
      (this.$refs.attributesModal as AttributesModal).dismiss();
    } catch (e) {
      handleError(this.$toasted, this.$i18n, e);
    } finally {
      this.savingAttributes = false;
    }
  }

  private async downloadCardPdf(): Promise<void> {
    try {
      const file = await this.state.printApi.downloadPdfByCardGuid(this.card.id);
      blobDownload(file.data, file.fileName ?? ('card' + this.card.id + '_' + formatDateTimeSeconds(new Date()) + '.pdf'));
    } catch (e) {
      handleError(this.$toasted, this.$i18n, e);
    }
  }

  private async exportCard(): Promise<void> {
    let data = '';
    data += this.$t('txtName') + ': ' + this.card.name + '\n';
    data += this.$t('txtBoard') + ': ' + this.board.name + '\n';
    data += this.$t('txtFlow') + ': ' + this.flow.name + '\n';
    data += this.$t('btnLabels') + ': ' + (this.card.cardLabels?.length ? this.card.cardLabels.map(e => e.label?.id).join(', ') : '(' + this.$t('txtEmpty') + ')') + '\n';
    data += this.$t('txtCreated') + ': ' + this.formatDateTime(this.card.createdOn) + '\n';
    data += this.$t('txtDueDate') + ': ' + (this.card.dueDate ? this.formatDateTime(this.card.dueDate) : ('(' + this.$t('txtEmpty') + ')')) + '\n';
    data += this.$t('txtResponsibles') + ': ' + (this.responsible.length ? this.responsible : ('(' + this.$t('txtNobody') + ')')) + '\n';
    data += this.$t('txtDescription') + ': ' + (this.card.description?.length ? this.card.description + '\n' : '(' + this.$t('txtEmpty') + ')') + '\n';
    data += this.$t('txtTasks') + ': ' + (this.card.taskItems?.length
        ? ((this.card.taskItems.filter(e => e.status === 'DONE').length) + '/' + (this.card.taskItems.length))
        : ('(' + this.$t('txtEmpty') + ')')) + '\n';
    this.card.taskItems?.forEach(task => {
      data += '  ' + (task.status === 'DONE' ? '[x]' : '[_]') + ' ' + task.name + '\n';
    });
    data += this.$t('txtComments') + ': ' + (this.card.comments?.length ? this.card.comments.length : ('(' + this.$t('txtEmpty') + ')')) + '\n';
    this.card.comments?.forEach(comment => {
      let user = comment.aspNetUser && (comment.aspNetUser.firstName + ' ' + comment.aspNetUser.lastName);
      data += '  > ' + (user ?? this.$t('txtUnknownUser')) + ': ' + comment.text + '\n';
      comment.commentReplies?.forEach(reply => {
        let user = reply.aspNetUser && (reply.aspNetUser.firstName + ' ' + reply.aspNetUser.lastName);
        data += '  >> ' + (user ?? this.$t('txtUnknownUser')) + ': ' + reply.text + '\n';
      });
    });
    let media = await this.getMedia();
    data += this.$t('txtAttachments') + ': ' + (media.length ? media.length : '(' + this.$t('txtEmpty') + ')') + '\n';
    media.forEach(media => {
      data += '  ' + media.publicUrl + '\n';
    });
    navigator.clipboard.writeText(data);
    this.$toasted.info(this.$t('txtCopiedToClipboard').toString());
  }

  private async publicLinkCard(): Promise<void> {
    if (!this.card.isPublic) {
      await this.addPublicAccess();
    }

    let url = window.location.protocol + '//'
        + window.location.hostname
        + (window.location.port?.length ? ':' + window.location.port : '')
        + window.application.baseUrl
        + 'Boards/Public/' + this.card.id;
    navigator.clipboard.writeText(url);
    this.$toasted.info(this.$t('txtCopiedToClipboard').toString());
  }

  private async addPublicAccess(): Promise<void> {
    let req = new PublicRequest();
    req.cardId = this.card.id!;
    req.isPublic = true;
    this.state.cardsAPI.updatePublic(req)
        .then(_ => {
          this.$toasted.info(this.$t('kanbanCardNowPublic').toString());
          this.card.isPublic = true;
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private async removePublicAccess(): Promise<void> {
    let req = new PublicRequest();
    req.cardId = this.card.id!;
    req.isPublic = false;
    this.state.cardsAPI.updatePublic(req)
        .then(_ => {
          this.$toasted.info(this.$t('kanbanCardNotPublicAnymore').toString());
          this.card.isPublic = false;
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private boardChanged(board: Board, flow: Ticket): void {//TODO check function
    if (!board.tickets?.filter(t => t.id === flow.id).length) {//check if flow is in board
      this.$toasted.error(this.$t('txtNoTargetFlow').toString());
      return;
    }

    let oldFlowId = this.card.ticketId;
    let newFlowId = flow.id;

    //save to backend
    this.state.cardsAPI.move2Board(board.id!, flow.id!, this.card)
        .then(res => {
          this.$toasted.info(this.$t('txtSaved').toString());

          this.card.ticketId = newFlowId;
          this.card.positionNo = res.result!.positionNo;
          let oldflow = this.board.tickets?.find(e => e.id === oldFlowId);
          if (oldflow) {
            let index = (oldflow.cards ?? []).findIndex(e => e.id === this.card.id);
            if (index > -1) {
              //remove
              oldflow.cards?.splice(index, 1);

              //add
              if (!flow.cards)
                flow.cards = [];
              flow.cards.push(this.card);
            }
          }
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private addShared(event: { account: Account, card: Card, board: Board | null }): void {
    this.state.sharedCardAPI.create(event.card.id!, event.account.accountId!, event.board?.id! ?? 0)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          let info = this.getSharedInfoCard(this.state, event.card);
          info.accountIds.push(event.account.accountId!);

          //self share
          if (event.account.accountId! === this.state.accounts?.selectedItem!) {
            //add self shared info
            info.targetSelfShareBoard = event.board?.id;
            info.targetSelfShareFlow = undefined;

            // -> create inboxes if necessary and add card
            let boardid = event.board?.id ?? 0;
            let board = this.state.boards.find(b => b.id === boardid);
            if (!board) {
              board = new Board({ id: 0, name: 'Inbox', tickets: [] });
              this.state.boards.unshift(board);
            }
            let flow = board.tickets?.find(f => f.id === 0);
            if (!flow) {
              flow = new Ticket({ id: 0, name: 'Inbox', cards: [], archived: false });
              board.tickets?.unshift(flow);
            }
            flow.cards?.push(event.card);
          }
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private removeShared(event: { account: Account, card: Card }): void {
    this.state.sharedCardAPI.delete(event.card.id!, event.account.accountId!)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          const info = this.getSharedInfoCard(this.state, event.card);
          const index = info.accountIds.findIndex(e => e == event.account.accountId!);
          if (index > -1)
            info.accountIds.splice(index, 1);

          //self unshare
          if (event.account.accountId! === this.state.accounts?.selectedItem!) {
            for (let b = 0; b < this.state.boards.length; b++) {
              for (let f = 0; f < (this.state.boards[b].tickets!.length ?? 0); f++) {
                for (let c = 0; c < (this.state.boards[b].tickets![f].cards!.length ?? 0); c++) {
                  if (this.state.boards[b].id! === (info.targetSelfShareBoard ?? 0)
                      && this.state.boards[b].tickets![f].id! === (info.targetSelfShareFlow ?? 0)
                      && this.state.boards[b].tickets![f].cards![c].id! === event.card.id) {
                    this.state.boards[b].tickets![f].cards!.splice(c, 1);
                  }
                }
              }
            }

            //remove self shared info
            info.targetSelfShareBoard = undefined;
            info.targetSelfShareFlow = undefined;
          }
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private archive(): void {
    if (confirm(this.$t('txtInfoArchive').toString())) {
      let flow = this.board.tickets?.find(e => e.id === this.card.ticketId);
      if (flow) {
        let index = (flow.cards ?? []).findIndex(e => e.id === this.card.id);
        if (index > -1) {
          flow.cards?.splice(index, 1);
        }
      }

      this.card.archived = true;

      //save to backend
      this.state.cardsAPI.archive(this.card)
          .then(_ => this.$toasted.info(this.$t('txtArchived').toString()))
          .catch(e => this.$toasted.error(this.$t('txtError') + ': ' + this.extractError(e.result.errorMsg)));
    }
  }

  private unarchive(): void {
    let flow = this.board.tickets?.find(e => e.id === this.card.ticketId);
    if (flow) {
      let index = (flow.cards ?? []).findIndex(e => e.id === this.card.id);
      if (index > -1) {
        flow.cards?.splice(index, 1);
      }
    }

    this.card.archived = false;

    //save to backend
    this.state.cardsAPI.archive(this.card)
        .then(_ => this.$toasted.info(this.$t('txtArchived').toString()))
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private deleteCard(): void {
    if (confirm(this.$t('txtInfoDelete').toString())) {
      this.state.cardsAPI.delete(this.card.id!)
          .then(_ => {
            this.$toasted.info(this.$t('txtDeleted').toString());
            let index = (this.flow.cards ?? []).findIndex(e => e.id === this.card.id);
            if (index > -1) {
              this.flow.cards?.splice(index, 1);
            }
          })
          .catch(e => handleError(this.$toasted, this.$i18n, e));
    }
  }

  private canDrop(file: File): boolean {
    return true;
  }

  private copyCard(): void {
    this.state.cardsAPI.duplicate(this.card.id!)
        .then(card => {
          this.$toasted.info(this.$t('kanban.card.duplicated').toString());
          this.flow.cards ??= [];
          this.flow.cards.push(card);
          this.closeModal();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  //media
  private async getMedia(): Promise<Media[]> {
    if (this.media !== null) {
      return this.media;
    }

    try {
      this.media = (await this.state.attachmentAPI.getAttachmentMedias(this.card.id!)).result!;
    } catch (ex) {
      console.log(ex);
    }

    return this.media!;
  }

  //util
  private formatDateTime(d: Date | undefined): string {
    if (!d)
      return '';
    if (window.application.kanban.kanbanshowtime) {
      return formatDateTime(d);
    } else {
      return formatDate(d);
    }
  }

  private extractError(e): string {
    if (e?.result?.errorMsg) {
      return e.result.errorMsg;
    }
    return JSON.stringify(e);
  }
}

